import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FiActivity, FiRefreshCw, FiSave } from 'react-icons/fi';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
  ReferenceArea,
  ReferenceLine,
} from 'recharts';
import api from '../services/api';
import '../components/Button.css';
import './ShopMonitor.css';

const PROBE_LABELS = {
  home: 'Homepage',
  pdp: 'Productpagina',
  woo_api: 'Woo REST API',
};

function formatProbeLabel(probe) {
  return PROBE_LABELS[probe] || probe;
}

function formatTimeShort(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString('nl-NL', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatChartTime(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString('nl-NL', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
}

function parseMonitorTime(isoOrMs) {
  if (isoOrMs == null || isoOrMs === '') return new Date(NaN);
  if (typeof isoOrMs === 'number') return new Date(isoOrMs);
  const s = String(isoOrMs).trim();
  if (!s) return new Date(NaN);
  if (s.includes('T')) return new Date(s);
  return new Date(s.replace(' ', 'T'));
}

function formatChartTimeDetailed(isoOrMs) {
  const d = parseMonitorTime(isoOrMs);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('nl-NL', {
    weekday: 'short',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function tooltipTimeLabel(row, label) {
  if (row?.time) return formatChartTimeDetailed(row.time);
  if (row?.timeMs != null) return formatChartTimeDetailed(row.timeMs);
  if (typeof label === 'number' && !Number.isNaN(label)) return formatChartTimeDetailed(label);
  if (typeof label === 'string' && label.trim()) {
    const asNum = Number(label);
    if (!Number.isNaN(asNum) && label.length >= 12) return formatChartTimeDetailed(asNum);
    return formatChartTimeDetailed(label);
  }
  return '—';
}

const LOAD_EVENT_FILL = {
  stock_sync: 'rgba(129, 140, 248, 0.22)',
  sales_sync: 'rgba(167, 139, 250, 0.22)',
  shopify_stock: 'rgba(244, 114, 182, 0.22)',
  product_import: 'rgba(251, 146, 60, 0.28)',
};

function toTimeMs(iso) {
  if (!iso) return null;
  const t = new Date(iso).getTime();
  return Number.isNaN(t) ? null : t;
}

function levelLabel(level) {
  if (level === 'ok') return 'OK';
  if (level === 'warn') return 'Traag';
  if (level === 'crit') return 'Zeer traag';
  if (level === 'down') return 'Probleem';
  return 'Onbekend';
}

export default function ShopMonitor() {
  const [summary, setSummary] = useState(null);
  const [config, setConfig] = useState(null);
  const [form, setForm] = useState(null);
  const [series, setSeries] = useState(null);
  const [recent, setRecent] = useState([]);
  const [hours, setHours] = useState(24);
  const [chartProbe, setChartProbe] = useState('home');
  const [metric, setMetric] = useState('ttfb_ms');
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [showSyncs, setShowSyncs] = useState(true);
  const [showPeaks, setShowPeaks] = useState(true);

  const loadAll = useCallback(async () => {
    setError('');
    try {
      const [sumRes, cfgRes, seriesRes, recentRes] = await Promise.all([
        api.get('/site-monitor/summary'),
        api.get('/site-monitor/config'),
        api.get(`/site-monitor/series?hours=${hours}&probe=${chartProbe}&metric=${metric}`),
        api.get('/site-monitor/recent?limit=25'),
      ]);
      setSummary(sumRes.data);
      setConfig(cfgRes.data);
      setForm(cfgRes.data);
      setSeries(seriesRes.data);
      setRecent(recentRes.data?.rows || []);
    } catch (e) {
      setError(e.response?.data?.error || e.message || 'Laden mislukt');
    } finally {
      setLoading(false);
    }
  }, [hours, chartProbe, metric]);

  useEffect(() => {
    loadAll();
    const t = setInterval(loadAll, 2 * 60 * 1000);
    return () => clearInterval(t);
  }, [loadAll]);

  const chartData = useMemo(() => {
    const pts = series?.points || [];
    return pts.map((p) => ({
      ...p,
      timeMs: toTimeMs(p.time),
      label: formatChartTime(p.time),
      value: metric === 'total_ms' ? p.total_ms : p.ttfb_ms,
    }));
  }, [series, metric]);

  const loadEvents = series?.load_events || [];
  const warnMs = series?.warn_ms;
  const critMs = series?.crit_ms;

  const renderPeakDot = (props) => {
    const { cx, cy, payload } = props;
    if (!showPeaks || !payload?.is_peak || cx == null || cy == null) return null;
    return <circle cx={cx} cy={cy} r={5} fill="#ef4444" stroke="#fff" strokeWidth={1.5} />;
  };

  const chartTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    const row = payload[0]?.payload;
    const syncsAt = showSyncs
      ? loadEvents.filter((ev) => {
          const s = toTimeMs(ev.started_at);
          const f = toTimeMs(ev.finished_at || ev.started_at);
          const x = row?.timeMs;
          if (s == null || x == null) return false;
          return x >= s && x <= (f != null ? f : s) + 60000;
        })
      : [];
    const metricLabel = metric === 'total_ms' ? 'Totaal' : 'TTFB';
    const otherMs = metric === 'total_ms' ? row?.ttfb_ms : row?.total_ms;
    const otherLabel = metric === 'total_ms' ? 'TTFB' : 'Totaal';
    const okPct =
      row?.ok_ratio != null ? `${Math.round(row.ok_ratio * 100)}% geslaagd` : null;
    return (
      <div className="shop-monitor-chart-tooltip">
        <div className="shop-monitor-chart-tooltip-time">{tooltipTimeLabel(row, label)}</div>
        <div className="shop-monitor-chart-tooltip-meta">
          {formatProbeLabel(chartProbe)} · {metricLabel}
          {row?.samples > 1 ? ` · ${row.samples} metingen` : row?.samples === 1 ? ' · 1 meting' : ''}
        </div>
        <div className="shop-monitor-chart-tooltip-value">
          {payload[0].value != null ? `${payload[0].value} ms` : '—'}
        </div>
        {otherMs != null ? (
          <div className="shop-monitor-chart-tooltip-secondary">
            {otherLabel}: {otherMs} ms
          </div>
        ) : null}
        {okPct ? <div className="shop-monitor-chart-tooltip-secondary">{okPct}</div> : null}
        {row?.is_peak && showPeaks ? <div className="shop-monitor-chart-tooltip-peak">Piek / traag</div> : null}
        {syncsAt.length > 0 ? (
          <div className="shop-monitor-chart-tooltip-sync">
            Inkoop: {syncsAt.map((e) => e.short_label || e.label).join(', ')}
          </div>
        ) : null}
      </div>
    );
  };

  const handleRunNow = async () => {
    setRunning(true);
    setError('');
    try {
      const res = await api.post('/site-monitor/run-now');
      if (res.data?.summary) setSummary(res.data.summary);
      await loadAll();
    } catch (e) {
      setError(e.response?.data?.error || e.message || 'Meting mislukt');
    } finally {
      setRunning(false);
    }
  };

  const handleSave = async () => {
    if (!form) return;
    setSaving(true);
    setError('');
    try {
      const res = await api.put('/site-monitor/config', form);
      setConfig(res.data);
      setForm(res.data);
      await loadAll();
    } catch (e) {
      setError(e.response?.data?.error || e.message || 'Opslaan mislukt');
    } finally {
      setSaving(false);
    }
  };

  if (loading && !summary) {
    return <p className="hint">4shine status laden…</p>;
  }

  const overall = summary?.overall || 'unknown';

  return (
    <div className="shop-monitor-page">
      <p className="shop-monitor-hint">
        Synthetic checks vanaf de Inkoop-backend (Railway). Meet TTFB en totale tijd; Cloudflare kan de homepage
        sneller laten lijken dan een echte bezoeker — gebruik daarom ook productpagina en Woo API.
      </p>

      {error ? (
        <div className="notice notice-warning" style={{ marginBottom: '1rem' }}>
          {error}
        </div>
      ) : null}

      <div className={`shop-monitor-overall level-${overall}`}>
        <FiActivity aria-hidden />
        Totaal: {levelLabel(overall)}
        {config && !config.enabled ? ' (monitor uit)' : null}
      </div>

      <div className="shop-monitor-status-row">
        {(summary?.probes || []).map((p) => (
          <div key={p.probe} className={`shop-monitor-tile level-${p.level}`}>
            <h3>{formatProbeLabel(p.probe)}</h3>
            <div className="metric">
              {p.ttfb_ms != null ? `${p.ttfb_ms} ms` : '—'}
              <span style={{ fontSize: '0.85rem', fontWeight: 500 }}> TTFB</span>
            </div>
            <div className="meta">
              {levelLabel(p.level)}
              {p.status_code ? ` · HTTP ${p.status_code}` : ''}
              {p.error ? ` · ${p.error}` : ''}
            </div>
            <div className="meta">Laatste: {formatTimeShort(p.checked_at)}</div>
          </div>
        ))}
      </div>

      <div className="shop-monitor-toolbar">
        <button type="button" className="btn btn-primary" onClick={handleRunNow} disabled={running}>
          <FiRefreshCw className={running ? 'spin' : ''} /> {running ? 'Meten…' : 'Nu meten'}
        </button>
        <label>
          Grafiek periode
          <select value={hours} onChange={(e) => setHours(Number(e.target.value))}>
            <option value={24}>24 uur</option>
            <option value={168}>7 dagen</option>
          </select>
        </label>
        <label>
          Lijn
          <select value={chartProbe} onChange={(e) => setChartProbe(e.target.value)}>
            <option value="home">Homepage</option>
            <option value="pdp">Productpagina</option>
            <option value="woo_api">Woo REST</option>
          </select>
        </label>
        <label>
          Meting
          <select value={metric} onChange={(e) => setMetric(e.target.value)}>
            <option value="ttfb_ms">TTFB (ms)</option>
            <option value="total_ms">Totaal (ms)</option>
          </select>
        </label>
        <label className="shop-monitor-check-row">
          <input type="checkbox" checked={showSyncs} onChange={(e) => setShowSyncs(e.target.checked)} />
          Inkoop-syncs in grafiek
        </label>
        <label className="shop-monitor-check-row">
          <input type="checkbox" checked={showPeaks} onChange={(e) => setShowPeaks(e.target.checked)} />
          Pieken markeren
        </label>
      </div>

      <div className="shop-monitor-chart-wrap">
        {chartData.length === 0 ? (
          <p className="hint">Nog geen meetpunten in deze periode. Klik &quot;Nu meten&quot; of wacht op de cron.</p>
        ) : (
          <>
            <ResponsiveContainer width="100%" height={340}>
              <LineChart data={chartData} margin={{ top: 28, right: 16, left: 0, bottom: 8 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis
                  dataKey="timeMs"
                  type="number"
                  domain={['dataMin', 'dataMax']}
                  tick={{ fontSize: 11 }}
                  tickFormatter={(ms) => formatChartTime(new Date(ms).toISOString())}
                />
                <YAxis tick={{ fontSize: 11 }} unit=" ms" />
                {showSyncs &&
                  loadEvents.map((ev) => {
                    const x1 = toTimeMs(ev.started_at);
                    let x2 = toTimeMs(ev.finished_at);
                    if (x1 == null) return null;
                    if (x2 == null || x2 <= x1) x2 = x1 + 60 * 1000;
                    return (
                      <ReferenceArea
                        key={ev.id}
                        x1={x1}
                        x2={x2}
                        strokeOpacity={0}
                        fill={LOAD_EVENT_FILL[ev.kind] || 'rgba(148, 163, 184, 0.2)'}
                        label={{
                          value: ev.short_label || ev.label,
                          position: 'insideTop',
                          fontSize: 10,
                          fill: '#4338ca',
                        }}
                      />
                    );
                  })}
                {warnMs != null ? (
                  <ReferenceLine y={warnMs} stroke="#f59e0b" strokeDasharray="4 4" label={{ value: 'Waarschuwing', fontSize: 10 }} />
                ) : null}
                {critMs != null ? (
                  <ReferenceLine y={critMs} stroke="#ef4444" strokeDasharray="4 4" label={{ value: 'Kritiek', fontSize: 10 }} />
                ) : null}
                <Tooltip content={chartTooltip} />
                <Legend />
                <Line
                  type="monotone"
                  dataKey="value"
                  name={metric === 'total_ms' ? 'Totaal' : 'TTFB'}
                  stroke="#0d9488"
                  strokeWidth={2}
                  dot={showPeaks ? renderPeakDot : false}
                  activeDot={{ r: 5 }}
                  connectNulls
                />
              </LineChart>
            </ResponsiveContainer>
            {showSyncs && loadEvents.length > 0 ? (
              <div className="shop-monitor-sync-legend">
                <span className="shop-monitor-sync-legend-title">Inkoop-taken in deze periode:</span>
                {loadEvents.slice(-12).map((ev) => (
                  <span key={ev.id} className="shop-monitor-sync-chip" title={ev.label}>
                    {formatTimeShort(ev.started_at)} — {ev.short_label || ev.label}
                    {ev.status === 'error' ? ' (fout)' : ''}
                  </span>
                ))}
              </div>
            ) : null}
          </>
        )}
      </div>

      <details className="shop-monitor-settings">
        <summary style={{ cursor: 'pointer', fontWeight: 600 }}>Instellingen</summary>
        {form ? (
          <div style={{ marginTop: '12px' }}>
            <div className="shop-monitor-form-grid">
              <label className="shop-monitor-check-row">
                <input
                  type="checkbox"
                  checked={!!form.enabled}
                  onChange={(e) => setForm((f) => ({ ...f, enabled: e.target.checked }))}
                />
                Monitor actief (cron op server)
              </label>
              <label className="shop-monitor-check-row">
                <input
                  type="checkbox"
                  checked={!!form.telegram_alerts}
                  onChange={(e) => setForm((f) => ({ ...f, telegram_alerts: e.target.checked }))}
                />
                Telegram bij 2× achter elkaar traag/down (zelfde bot als orders)
              </label>
              <label>
                Homepage URL
                <input
                  type="url"
                  value={form.home_url || ''}
                  onChange={(e) => setForm((f) => ({ ...f, home_url: e.target.value }))}
                />
              </label>
              <label>
                Productpagina URL (PDP)
                <input
                  type="url"
                  value={form.pdp_url || ''}
                  onChange={(e) => setForm((f) => ({ ...f, pdp_url: e.target.value }))}
                />
              </label>
              <label>
                Interval (minuten)
                <input
                  type="number"
                  min={1}
                  max={60}
                  value={form.interval_min ?? 5}
                  onChange={(e) => setForm((f) => ({ ...f, interval_min: Number(e.target.value) }))}
                />
              </label>
              <label>
                Waarschuwing TTFB (ms)
                <input
                  type="number"
                  min={500}
                  value={form.warn_ttfb_ms ?? 2000}
                  onChange={(e) => setForm((f) => ({ ...f, warn_ttfb_ms: Number(e.target.value) }))}
                />
              </label>
              <label>
                Kritiek TTFB (ms)
                <input
                  type="number"
                  min={1000}
                  value={form.crit_ttfb_ms ?? 5000}
                  onChange={(e) => setForm((f) => ({ ...f, crit_ttfb_ms: Number(e.target.value) }))}
                />
              </label>
            </div>
            <button
              type="button"
              className="btn btn-primary"
              style={{ marginTop: '12px' }}
              onClick={handleSave}
              disabled={saving}
            >
              <FiSave /> {saving ? 'Opslaan…' : 'Instellingen opslaan'}
            </button>
          </div>
        ) : null}
      </details>

      <h3 className="section-title">Laatste metingen</h3>
      <div className="shop-monitor-table-wrap">
        <table className="data-table">
          <thead>
            <tr>
              <th>Tijd</th>
              <th>Check</th>
              <th>Status</th>
              <th>TTFB</th>
              <th>Totaal</th>
              <th>Fout</th>
            </tr>
          </thead>
          <tbody>
            {recent.map((row, i) => (
              <tr key={`${row.checked_at}-${row.probe}-${i}`}>
                <td>{formatTimeShort(row.checked_at)}</td>
                <td>{formatProbeLabel(row.probe)}</td>
                <td>{row.ok ? 'OK' : 'Fout'}</td>
                <td>{row.ttfb_ms != null ? `${row.ttfb_ms} ms` : '—'}</td>
                <td>{row.total_ms != null ? `${row.total_ms} ms` : '—'}</td>
                <td>{row.error || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
